import {
    Component,
    inject,
    Input,
    OnInit,
    ChangeDetectionStrategy
} from "@angular/core";
import { AbstractControl, FormBuilder, ValidationErrors, Validators } from "@angular/forms";
import { Observable, startWith, tap } from "rxjs";
import { DigitalSignaturDeltagareTyper, DokumentTyp, Kontakter, Signeringsmetoder } from "~app/common/models/constants";
import { RaskCommonModule } from "~app/common/rask-common.module";
import { ValidationUtils } from "~app/common/services/ValidationUtils";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { VirkeskopskontraktDigitalSigneringViewModel } from "~app/features/virkeskop/models/kontrakt-models";
import { UtskriftTyp } from "~app/features/virkeskop/models/utskrift/utskrift-models";
import { DigitalSigneringMediatorService } from "~app/features/virkeskop/services/digitalsignering-mediator.service";
import { DigitalSigneringFormGroup, LeverantorFormGroup } from "../../digital-signernig-form";
import { IVKDigitalsignaturDeltagare } from "../kontrakt-signering-model";

@Component({
    selector: "rask-kontrakt-signering-leverantor",
    imports: [RaskCommonModule],
    templateUrl: "./kontrakt-signering-leverantor.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: "./kontrakt-signering-leverantor.component.scss"
})
export class KontraktSigneringLeverantorComponent implements OnInit {

    protected signeringsmetoder = Signeringsmetoder.alla;
    protected leverantorFormGroup: LeverantorFormGroup;
    protected makeMakaVisible$: Observable<boolean>;
    protected alternativKontaktperson$: Observable<boolean>;

    @Input() protected readonly deltagare: IVKDigitalsignaturDeltagare;
    @Input() protected readonly aktivSignering: VirkeskopskontraktDigitalSigneringViewModel;
    @Input() private readonly form: DigitalSigneringFormGroup;
    @Input() private readonly dokumentTyp: UtskriftTyp;

    private readonly fb = inject(FormBuilder);

    private readonly digitalSigneringMediatorService = inject(DigitalSigneringMediatorService);

    ngOnInit(): void {

        this.leverantorFormGroup = this.leverantorFormArray.controls.find(c => c.controls.leverantorGuid.value === this.deltagare.deltagarGuid);
        const previousMakeMaka = this.aktivSignering?.deltagare?.find(x => x.leverantorId === this.deltagare.leverantorId && x.deltagarTyp === "MakeMaka");
        const previousLeverantor = this.aktivSignering?.deltagare?.find(x => x.leverantorId === this.deltagare.leverantorId && x.deltagarTyp === "Leverantor");
        const telefonnummer = ObjectUtils.hasValue(this.deltagare.kontaktPersonMobiltelefon) ? this.deltagare.kontaktPersonMobiltelefon : this.deltagare.telefon;
        if (!this.leverantorFormGroup) {
            this.leverantorFormGroup = this.fb.group({
                leverantorGuid: [this.deltagare.deltagarGuid],
                namn: [this.deltagare.namn],
                persOrgNummer: [this.deltagare.persOrgNummer],
                deltagarTyp: [this.deltagare.deltagarTyp],
                epost: [
                    this.deltagare.epost,
                    [
                        Validators.required,
                        Validators.email,
                        Validators.pattern(Kontakter.EpostRegularExpression)
                    ]
                ],
                telefon: [ObjectUtils.hasValue(this.deltagare.kontaktPersonMobiltelefon) ? this.deltagare.kontaktPersonMobiltelefon : this.deltagare.telefon],
                signeringsmetod: [
                    ObjectUtils.stringHasValue(previousLeverantor?.signeringsmetod) ?
                        previousLeverantor.signeringsmetod : Signeringsmetoder.bankid,
                    [Validators.required]
                ],
                smsNummer: [
                    ObjectUtils.stringHasValue(previousLeverantor?.smsNummer) ? previousLeverantor.smsNummer : telefonnummer,
                    [this.validatePhonenumberLeverantor]
                ],
                selected: [
                    {
                        value: true,
                        disabled: !this.deltagare.epost
                    }
                ],
                signeringMakeMaka: [!!previousMakeMaka],
                alternativKontaktperson: [previousLeverantor?.alternativKontaktperson],
                makeGodkannandeKravsEj: [this.deltagare.makeGodkannandeKravsEj],
                inbjudningsOrdning: [this.deltagare.inbjudningsOrdning],
                makeMaka: this.fb.group({
                    namn: [previousMakeMaka?.makeMakaNamn],
                    epost: [
                        previousMakeMaka?.makeMakaEpost,
                        [
                            Validators.required,
                            Validators.email,
                            Validators.pattern(Kontakter.EpostRegularExpression)
                        ]
                    ],
                    signeringsmetod: [
                        ObjectUtils.stringHasValue(previousMakeMaka?.signeringsmetod) ?
                            previousMakeMaka?.signeringsmetod : Signeringsmetoder.bankid,
                        [Validators.required]
                    ],
                    smsNummer: [
                        previousMakeMaka?.smsNummer,
                        [this.validatePhonenumberMakeMaka]
                    ]
                })
            });
            this.leverantorFormArray.push(this.leverantorFormGroup);
            this.signingMetodChange();
        }

        this.makeMakaVisible$ = this.leverantorFormGroup.controls.signeringMakeMaka.valueChanges.pipe(
            startWith(this.leverantorFormGroup.controls.signeringMakeMaka.value),
            tap(signeringMakeMaka => {
                this.signingMetodMakeMakaChange();
                return signeringMakeMaka ? this.leverantorFormGroup.controls.makeMaka.enable() : this.leverantorFormGroup.controls.makeMaka.disable();
            }));

        this.alternativKontaktperson$ = this.leverantorFormGroup.controls.alternativKontaktperson.valueChanges.pipe(
            startWith(this.leverantorFormGroup.controls.alternativKontaktperson.value),
            tap(alternativKontaktperson => {
                // SQ false positive https://sonarsource.atlassian.net/browse/JS-854

                const opts = {
                    emitEvent: false,
                    onlySelf: true
                };
                if (alternativKontaktperson || this.deltagare.deltagarTyp === DigitalSignaturDeltagareTyper.extraDeltagare) {
                    this.leverantorFormGroup.controls.namn.enable(opts);
                    this.leverantorFormGroup.controls.epost.enable(opts);
                } else {
                    this.leverantorFormGroup.controls.namn.disable(opts);
                    this.leverantorFormGroup.controls.epost.disable(opts);
                }
            })
        );
    }

    signingMetodChange() {
        const { smsNummer, signeringsmetod } = this.leverantorFormGroup.controls;
        if(signeringsmetod?.value === Signeringsmetoder.sms ) {
            smsNummer.enable();
        } else {
            smsNummer.disable();
        }
    }

    signingMetodMakeMakaChange() {
        const { smsNummer, signeringsmetod } = this.leverantorFormGroup.controls.makeMaka.controls;
        if(signeringsmetod?.value === Signeringsmetoder.sms ) {
            smsNummer.enable();
        } else {
            smsNummer.disable();
        }
    }

    validatePhonenumberMakeMaka = (c: AbstractControl): ValidationErrors | null => {
        if (this.leverantorFormGroup && this.leverantorFormGroup?.controls?.makeMaka?.controls.signeringsmetod?.value === Signeringsmetoder.sms) {
            return ValidationUtils.validatePhonenumber(c);
        }
        return null;
    };

    validatePhonenumberLeverantor = (c: AbstractControl): ValidationErrors | null => {
        if (this.leverantorFormGroup && this.leverantorFormGroup?.controls?.signeringsmetod?.value === Signeringsmetoder.sms) {
            return ValidationUtils.validatePhonenumber(c);
        }
        return null;
    };

    getSigneringsmetoder(): string[] {
        if(this.deltagare.deltagarTyp === DigitalSignaturDeltagareTyper.extraDeltagare) {
            return Signeringsmetoder.alla;
        }
        return [
            Signeringsmetoder.bankid,
            Signeringsmetoder.sms
        ];
    }

    removeDeltagare() {
        this.digitalSigneringMediatorService.removeMottagare(this.deltagare);
        this.leverantorFormArray.removeAt(this.leverantorFormArray.controls.findIndex(x => x.controls.leverantorGuid === this.leverantorFormGroup.controls.leverantorGuid));
    }

    protected get leverantorFormArray() {
        return this.form.controls.leverantorer;
    }

    protected showMakeMakeSelection(): boolean {
        return !this.leverantorFormGroup.controls.makeGodkannandeKravsEj.value && !this.isForetag() && this.dokumentTyp === DokumentTyp.Kontrakt;
    }

    protected showForetagSelection(): boolean {
        return this.isForetag() && (this.dokumentTyp === DokumentTyp.Kontrakt || this.dokumentTyp === DokumentTyp.SkogsvardsKontrakt);
    }

    protected isForetag(): boolean {
        return !this.deltagare.isPersonnummer;
    }

    protected getNamn(): any {

        // Här ska ändras
        if (this.leverantorFormGroup.controls.alternativKontaktperson.value) {
            return `${this.deltagare.namn} (Genom ${this.leverantorFormGroup.controls.namn.value ?? "..."})`;
        }

        if (this.isForetag()) {
            return `${this.deltagare.namn} (Genom ${this.deltagare.kontaktPersonNamn})`;
        }
        return this.deltagare.namn;
    }
}
